import * as THREE from 'three';

const THREAT_COLORS = {
  CRITICAL: new THREE.Color(0xff2255),
  HIGH: new THREE.Color(0xff6644),
  MEDIUM: new THREE.Color(0xffaa22),
  LOW: new THREE.Color(0x44dd66),
  CLEAN: new THREE.Color(0x22ccff),
  UNKNOWN: new THREE.Color(0x666688)
};

const GEOJSON_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

export class GlobeVisualization {
  constructor(canvas) {
    this.canvas = canvas;
    this.pins = [];
    this.pulseRings = [];
    this.scanBeams = [];
    this.borderLines = [];
    this.bordersVisible = true;
    this.bordersLoaded = false;
    this.mouseDown = false;
    this.mouseX = 0;
    this.mouseY = 0;
    this.targetRotX = 0.3;
    this.targetRotY = 0;
    this.autoRotate = true;
    this.clock = new THREE.Clock();

    this.init();
    this.animate();
  }

  init() {
    const rect = this.canvas.parentElement.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;

    // Scene
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x060810, 0.0008);

    // Camera
    this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 0.1, 1000);
    this.camera.position.z = 4;

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x060810, 1);

    // Globe group
    this.globeGroup = new THREE.Group();
    this.scene.add(this.globeGroup);

    this.createGlobe();
    this.createAtmosphere();
    this.createGrid();
    this.createStars();
    this.loadCountryBorders();

    // Lights
    const ambient = new THREE.AmbientLight(0x111122, 0.5);
    this.scene.add(ambient);
    const directional = new THREE.DirectionalLight(0x4488ff, 0.3);
    directional.position.set(5, 3, 5);
    this.scene.add(directional);

    // Events
    this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.canvas.addEventListener('mouseup', () => this.onMouseUp());
    this.canvas.addEventListener('wheel', (e) => this.onWheel(e));
    window.addEventListener('resize', () => this.onResize());
  }

  createGlobe() {
    // Wireframe sphere
    const geo = new THREE.SphereGeometry(1.5, 48, 48);
    const mat = new THREE.MeshPhongMaterial({
      color: 0x0a1628,
      wireframe: false,
      transparent: true,
      opacity: 0.85,
      shininess: 5
    });
    this.globe = new THREE.Mesh(geo, mat);
    this.globeGroup.add(this.globe);

    // Wireframe overlay
    const wireGeo = new THREE.SphereGeometry(1.502, 36, 36);
    const wireMat = new THREE.MeshBasicMaterial({
      color: 0x00ff88,
      wireframe: true,
      transparent: true,
      opacity: 0.06
    });
    this.wireframe = new THREE.Mesh(wireGeo, wireMat);
    this.globeGroup.add(this.wireframe);

    // Latitude lines
    for (let lat = -60; lat <= 60; lat += 30) {
      const curve = new THREE.EllipseCurve(0, 0, 
        Math.cos(lat * Math.PI / 180) * 1.505,
        Math.cos(lat * Math.PI / 180) * 1.505,
        0, 2 * Math.PI, false, 0);
      const points = curve.getPoints(64);
      const lineGeo = new THREE.BufferGeometry().setFromPoints(
        points.map(p => new THREE.Vector3(p.x, Math.sin(lat * Math.PI / 180) * 1.505, p.y))
      );
      const lineMat = new THREE.LineBasicMaterial({ color: 0x00ff88, transparent: true, opacity: 0.08 });
      this.globeGroup.add(new THREE.Line(lineGeo, lineMat));
    }

    // Longitude lines
    for (let lng = 0; lng < 360; lng += 30) {
      const points = [];
      for (let i = 0; i <= 64; i++) {
        const phi = (i / 64) * Math.PI;
        const theta = lng * Math.PI / 180;
        points.push(new THREE.Vector3(
          1.505 * Math.sin(phi) * Math.cos(theta),
          1.505 * Math.cos(phi),
          1.505 * Math.sin(phi) * Math.sin(theta)
        ));
      }
      const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
      const lineMat = new THREE.LineBasicMaterial({ color: 0x00ff88, transparent: true, opacity: 0.05 });
      this.globeGroup.add(new THREE.Line(lineGeo, lineMat));
    }
  }

  async loadCountryBorders() {
    try {
      const response = await fetch(GEOJSON_URL);
      const topoData = await response.json();

      // topojson to geojson conversion (inline, no extra dep)
      const countries = this.topojsonToGeojson(topoData, topoData.objects.countries);

      const borderMat = new THREE.LineBasicMaterial({
        color: 0x00ff88,
        transparent: true,
        opacity: 0.25,
        linewidth: 1
      });

      countries.features.forEach(feature => {
        const coords = feature.geometry.type === 'Polygon'
          ? [feature.geometry.coordinates]
          : feature.geometry.coordinates; // MultiPolygon

        coords.forEach(polygon => {
          polygon.forEach(ring => {
            const points = [];
            ring.forEach(([lng, lat]) => {
              points.push(this.latLngToVector3(lat, lng, 1.508));
            });
            if (points.length > 2) {
              const geo = new THREE.BufferGeometry().setFromPoints(points);
              const line = new THREE.Line(geo, borderMat);
              line.visible = this.bordersVisible;
              this.globeGroup.add(line);
              this.borderLines.push(line);
            }
          });
        });
      });

      this.bordersLoaded = true;
    } catch (err) {
      console.warn('Failed to load country borders:', err);
    }
  }

  // Minimal topojson → geojson converter
  topojsonToGeojson(topology, object) {
    const features = object.geometries.map(geom => {
      return {
        type: 'Feature',
        geometry: this.decodeGeometry(topology, geom),
        properties: geom.properties || {}
      };
    });
    return { type: 'FeatureCollection', features };
  }

  decodeGeometry(topology, geom) {
    const { arcs: topoArcs, transform } = topology;

    function decodeArc(arcIdx) {
      const arc = topoArcs[arcIdx < 0 ? ~arcIdx : arcIdx];
      let x = 0, y = 0;
      const coords = arc.map(([dx, dy]) => {
        x += dx;
        y += dy;
        if (transform) {
          return [
            x * transform.scale[0] + transform.translate[0],
            y * transform.scale[1] + transform.translate[1]
          ];
        }
        return [x, y];
      });
      if (arcIdx < 0) coords.reverse();
      return coords;
    }

    function decodeRing(arcRefs) {
      let ring = [];
      arcRefs.forEach(ref => {
        const decoded = decodeArc(ref);
        // Skip first point of subsequent arcs to avoid duplicates
        ring = ring.concat(ring.length ? decoded.slice(1) : decoded);
      });
      return ring;
    }

    if (geom.type === 'Polygon') {
      return {
        type: 'Polygon',
        coordinates: geom.arcs.map(ring => decodeRing(ring))
      };
    } else if (geom.type === 'MultiPolygon') {
      return {
        type: 'MultiPolygon',
        coordinates: geom.arcs.map(polygon =>
          polygon.map(ring => decodeRing(ring))
        )
      };
    }
    return { type: geom.type, coordinates: [] };
  }

  setBordersVisible(visible) {
    this.bordersVisible = visible;
    this.borderLines.forEach(line => {
      line.visible = visible;
    });
  }

  createAtmosphere() {
    const geo = new THREE.SphereGeometry(1.6, 48, 48);
    const mat = new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vNormal;
        void main() {
          float intensity = pow(0.65 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 3.0);
          gl_FragColor = vec4(0.0, 1.0, 0.53, 1.0) * intensity * 0.4;
        }
      `,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true
    });
    this.atmosphere = new THREE.Mesh(geo, mat);
    this.globeGroup.add(this.atmosphere);
  }

  createGrid() {
    // Background grid
    const gridGeo = new THREE.PlaneGeometry(20, 20, 40, 40);
    const gridMat = new THREE.MeshBasicMaterial({
      color: 0x00ff88,
      wireframe: true,
      transparent: true,
      opacity: 0.015
    });
    const grid = new THREE.Mesh(gridGeo, gridMat);
    grid.rotation.x = -Math.PI / 2;
    grid.position.y = -3;
    this.scene.add(grid);
  }

  createStars() {
    const starGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(3000);
    for (let i = 0; i < 3000; i++) {
      positions[i] = (Math.random() - 0.5) * 50;
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const starMat = new THREE.PointsMaterial({ color: 0x445566, size: 0.05, transparent: true, opacity: 0.6 });
    this.scene.add(new THREE.Points(starGeo, starMat));
  }

  latLngToVector3(lat, lng, radius = 1.52) {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lng + 180) * (Math.PI / 180);
    return new THREE.Vector3(
      -radius * Math.sin(phi) * Math.cos(theta),
      radius * Math.cos(phi),
      radius * Math.sin(phi) * Math.sin(theta)
    );
  }

  addPin(lat, lng, threatLevel, domain) {
    const pos = this.latLngToVector3(lat, lng);
    const color = THREAT_COLORS[threatLevel] || THREAT_COLORS.UNKNOWN;

    // Pin dot
    const dotGeo = new THREE.SphereGeometry(0.02, 8, 8);
    const dotMat = new THREE.MeshBasicMaterial({ color });
    const dot = new THREE.Mesh(dotGeo, dotMat);
    dot.position.copy(pos);
    this.globeGroup.add(dot);

    // Vertical beam
    const beamHeight = 0.15 + Math.random() * 0.1;
    const beamGeo = new THREE.CylinderGeometry(0.003, 0.003, beamHeight, 4);
    const beamMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.7 });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    const up = pos.clone().normalize();
    beam.position.copy(pos).add(up.clone().multiplyScalar(beamHeight / 2));
    beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
    this.globeGroup.add(beam);

    // Pulse ring
    const ringGeo = new THREE.RingGeometry(0.01, 0.04, 32);
    const ringMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.5, side: THREE.DoubleSide });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.position.copy(pos);
    ring.lookAt(new THREE.Vector3(0, 0, 0));
    this.globeGroup.add(ring);
    this.pulseRings.push({ mesh: ring, baseScale: 1, maxScale: 3, speed: 0.5 + Math.random() * 0.5, time: Math.random() * Math.PI * 2 });

    // Glow sprite
    const glowMat = new THREE.SpriteMaterial({
      color,
      transparent: true,
      opacity: 0.3,
      blending: THREE.AdditiveBlending
    });
    const glow = new THREE.Sprite(glowMat);
    glow.position.copy(pos);
    glow.scale.set(0.1, 0.1, 1);
    this.globeGroup.add(glow);

    this.pins.push({ dot, beam, ring, glow, lat, lng, domain, threatLevel });
  }

  addScanBeam(lat, lng) {
    const pos = this.latLngToVector3(lat, lng, 1.55);
    const beamGeo = new THREE.ConeGeometry(0.08, 0.4, 16, 1, true);
    const beamMat = new THREE.MeshBasicMaterial({
      color: 0x00ff88,
      transparent: true,
      opacity: 0.3,
      wireframe: true,
      side: THREE.DoubleSide
    });
    const beam = new THREE.Mesh(beamGeo, beamMat);
    const up = pos.clone().normalize();
    beam.position.copy(pos).add(up.clone().multiplyScalar(0.2));
    beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
    this.globeGroup.add(beam);
    this.scanBeams.push({ mesh: beam, createdAt: this.clock.getElapsedTime() });
  }

  removeScanBeams() {
    this.scanBeams.forEach(b => {
      this.globeGroup.remove(b.mesh);
      b.mesh.geometry.dispose();
      b.mesh.material.dispose();
    });
    this.scanBeams = [];
  }

  onMouseDown(e) {
    this.mouseDown = true;
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
    this.autoRotate = false;
  }

  onMouseMove(e) {
    if (!this.mouseDown) return;
    const dx = e.clientX - this.mouseX;
    const dy = e.clientY - this.mouseY;
    this.targetRotY += dx * 0.005;
    this.targetRotX += dy * 0.005;
    this.targetRotX = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.targetRotX));
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
  }

  onMouseUp() {
    this.mouseDown = false;
    setTimeout(() => { this.autoRotate = true; }, 3000);
  }

  onWheel(e) {
    e.preventDefault();
    this.camera.position.z = Math.max(2.5, Math.min(8, this.camera.position.z + e.deltaY * 0.005));
  }

  onResize() {
    const rect = this.canvas.parentElement.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    const t = this.clock.getElapsedTime();

    // Auto-rotate
    if (this.autoRotate) {
      this.targetRotY += 0.001;
    }

    // Smooth rotation
    this.globeGroup.rotation.x += (this.targetRotX - this.globeGroup.rotation.x) * 0.05;
    this.globeGroup.rotation.y += (this.targetRotY - this.globeGroup.rotation.y) * 0.05;

    // Pulse rings animation
    this.pulseRings.forEach(pr => {
      pr.time += 0.016 * pr.speed;
      const cycle = (Math.sin(pr.time) + 1) / 2;
      const scale = pr.baseScale + cycle * (pr.maxScale - pr.baseScale);
      pr.mesh.scale.set(scale, scale, 1);
      pr.mesh.material.opacity = 0.4 * (1 - cycle);
    });

    // Scan beams animation
    this.scanBeams.forEach(b => {
      const age = t - b.createdAt;
      b.mesh.rotation.y = age * 2;
      b.mesh.material.opacity = 0.3 * (0.5 + 0.5 * Math.sin(age * 4));
    });

    // Update HUD
    const rot = this.globeGroup.rotation;
    const latEl = document.getElementById('hud-lat');
    const lngEl = document.getElementById('hud-lng');
    if (latEl) latEl.textContent = (rot.x * 180 / Math.PI).toFixed(3);
    if (lngEl) lngEl.textContent = (rot.y * 180 / Math.PI).toFixed(3);

    this.renderer.render(this.scene, this.camera);
  }

  updateFromData(globeData) {
    const nodesEl = document.getElementById('hud-nodes');
    const threatsEl = document.getElementById('hud-threats');
    if (nodesEl) nodesEl.textContent = globeData.length;
    const threats = globeData.filter(d => d.threat === 'CRITICAL' || d.threat === 'HIGH').length;
    if (threatsEl) threatsEl.textContent = threats;

    globeData.forEach(d => {
      if (!this.pins.find(p => p.domain === d.domain)) {
        this.addPin(d.lat, d.lng, d.threat, d.domain);
      }
    });
  }
}
